// gtTemp.js (C1-EMAIL) — a candidate's temperature, worked out from two dates.
// Same rules as server/temperature.js:
//   Hot:   responded in the last 30 days
//   Warm:  responded 31 to 60 days ago, or contacted in the last 60 days
//   Cold:  contact either way before, nothing in 60 days
//   Never: never contacted
// Rows need last_contacted_at, last_contact_kind, last_responded_at, last_response_kind.

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const DAY = 86400000;
export const TEMPS = [
  { value: 'hot', label: 'Hot' },
  { value: 'warm', label: 'Warm' },
  { value: 'cold', label: 'Cold' },
  { value: 'never', label: 'Never contacted' },
];
const CONTACT = {
  email: 'emailed', linkedin: 'messaged on LinkedIn', call: 'called', meeting: 'met', note: 'contacted',
  form: 'sent a form', update: 'sent an update', interview_invite: 'invited to interview',
};
const RESPONSE = {
  email_reply: 'replied to an email', linkedin_reply: 'replied on LinkedIn', call: 'spoke on a call', meeting: 'met',
  note_reply: 'replied', form_answer: 'answered a form', applied: 'applied', interview: 'did an interview',
};
const day = (v) => new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

export function tempOf(row) {
  const r = row && row.last_responded_at ? new Date(row.last_responded_at).getTime() : null;
  const c = row && row.last_contacted_at ? new Date(row.last_contacted_at).getTime() : null;
  const now = Date.now();
  const said = r ? (RESPONSE[row.last_response_kind] || 'responded') + ' ' + day(row.last_responded_at) : null;
  const did = c ? (CONTACT[row.last_contact_kind] || 'contacted') + ' ' + day(row.last_contacted_at) : null;
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  if (r && now - r <= 30 * DAY) return { key: 'hot', label: 'Hot', why: cap(said) };
  if ((r && now - r <= 60 * DAY) || (c && now - c <= 60 * DAY)) {
    const recentContact = c && now - c <= 60 * DAY && (!r || c > r);
    return { key: 'warm', label: 'Warm', why: recentContact ? cap(did) + (r && r < c ? ', last reply ' + day(row.last_responded_at) : ', no reply yet') : cap(said) };
  }
  if (r || c) {
    const last = r && (!c || r > c) ? said : did;
    return { key: 'cold', label: 'Cold', why: 'Nothing in 60 days. Last: ' + last };
  }
  return { key: 'never', label: 'Never contacted', why: 'Never contacted' };
}

export function TempDot({ row, label, size }) {
  const t = tempOf(row);
  return (
    <span className={'gttemp gttemp--' + t.key + (label ? ' gttemp--label' : '') + (size === 'lg' ? ' gttemp--lg' : '')} title={t.label + ': ' + t.why}>
      <i aria-hidden="true" />{label ? <span>{t.label}</span> : <span className="gttemp-sr">{t.label}</span>}
    </span>
  );
}

// C1-TEMPHIST: the record's chip, opening the temperature history. The panel
// is portalled to <body> so no header or card can clip it.
const TEMP_LABEL = { hot: 'Hot', warm: 'Warm', cold: 'Cold', never: 'Never contacted' };
export function TempHistory({ row, candidateId, authFetch }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const pop = useRef(null);
  const af = useRef(authFetch);
  af.current = authFetch;
  useEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const r = btn.current.getBoundingClientRect();
    setPos({ position: 'fixed', top: Math.round(r.bottom + 6), left: Math.round(Math.max(8, Math.min(r.left, window.innerWidth - 336))) });
    const close = (e) => {
      if (btn.current && btn.current.contains(e.target)) return;
      if (pop.current && pop.current.contains(e.target)) return;
      setOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    // C1-TIDY20: scrolling inside the panel's own list keeps it open.
    const away = (e) => { if (e && e.target instanceof Node && pop.current && pop.current.contains(e.target)) return; setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    let live = true;
    setErr(null);
    af.current('/api/candidates/' + candidateId + '/temperature-history')
      .then((d) => { if (live) setRows(d.history || []); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => {
      live = false;
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
    };
  }, [open, candidateId]);
  const t = tempOf(row);
  return (
    <>
      <button ref={btn} type="button" className="gttemp-histbtn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <TempDot row={row} label />
      </button>
      {open && pos && createPortal(
        <div ref={pop} className="gttemp-hist" style={pos} role="dialog" aria-label="Temperature history">
          <div className="gttemp-hist-now"><b>{t.label} now</b><span>{t.why}</span></div>
          {err && <div className="gttemp-hist-note">{err}</div>}
          {!err && rows === null && <div className="gttemp-hist-note">Loading</div>}
          {!err && rows && rows.length === 0 && <div className="gttemp-hist-note">No history yet. Changes are recorded every hour.</div>}
          {!err && rows && rows.length > 0 && (
            <ol className="gttemp-hist-list">
              {rows.map((h, i) => (
                <li key={i}>
                  <span className={'gttemp gttemp--' + h.temperature}><i aria-hidden="true" /></span>
                  <span>{(TEMP_LABEL[h.temperature] || h.temperature) + ' ' + (i === 0 ? 'since ' + day(h.changed_at) : day(h.changed_at) + ' to ' + day(rows[i - 1].changed_at)) + (h.baseline ? ' (when records began)' : '')}</span>
                </li>
              ))}
            </ol>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

// C1-TEMPHIST: "up from 2 thirty days ago" on a bench header, once records
// reach back that far. Shows nothing before then.
export function BenchTempTrend({ benchId, authFetch, hotNow, days = 30 }) {
  const [trend, setTrend] = useState(null);
  const af = useRef(authFetch);
  af.current = authFetch;
  useEffect(() => {
    let live = true;
    af.current('/api/benches/' + benchId + '/temperature-trend?days=' + days)
      .then((d) => { if (live) setTrend(d); })
      .catch(() => null);
    return () => { live = false; };
  }, [benchId, days, hotNow]);
  if (!trend || !trend.then) return null;
  const was = trend.then.hot;
  const when = days === 30 ? 'thirty days ago' : days + ' days ago';
  const text = hotNow > was ? 'up from ' + was + ' ' + when : hotNow < was ? 'down from ' + was + ' ' + when : 'same as ' + when;
  return <span className="gttemp-trend">{'\u00b7 ' + text}</span>;
}

// "18 of 40 hot, 9 warm" for a bench header.
export function tempCounts(rows) {
  const n = { hot: 0, warm: 0, cold: 0, never: 0 };
  (rows || []).forEach((r) => { n[tempOf(r).key] += 1; });
  return n;
}
